import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Material Dashboard 2 React base styles
import breakpoints from "assets/theme/base/breakpoints";

// Images
import backgroundImage from "assets/images/bg-profile.jpeg";

function HeaderNoMargin({ children, profileData, tabValue, onTabChange }) {
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [internalTabValue, setInternalTabValue] = useState(0);

  // Extract student data
  const student = profileData?.student || {};
  const branch = profileData?.branch || {};
  const batch = profileData?.batch || {};
  const avatarSrc = student.photo_url || "";
  const avatarFallbackSrc = student.photo_fallback_url || "";
  const [currentAvatarSrc, setCurrentAvatarSrc] = useState(avatarSrc);

  useEffect(() => {
    setCurrentAvatarSrc(avatarSrc);
  }, [avatarSrc]);

  useEffect(() => {
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    window.addEventListener("resize", handleTabsOrientation);
    handleTabsOrientation();

    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const currentTabValue = typeof tabValue === "number" ? tabValue : internalTabValue;
  const handleSetTabValue = (event, newValue) => {
    setInternalTabValue(newValue);
    if (onTabChange) onTabChange(newValue);
  };

  const avatarName = student.student_name || student.student_rollno || "Student";

  return (
    <MDBox position="relative" mb={5}>
      <MDBox
        display="flex"
        alignItems="center"
        position="relative"
        minHeight="5.75rem"
        borderRadius="xl"
      />
      <Card
        sx={{
          position: "relative",
          mt: { xs: -7, sm: -8, md: -9, lg: -10 },
          mx: { xs: 1.5, sm: 2, md: 3 },
          py: 2,
          px: 2,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <MDAvatar
              src={currentAvatarSrc}
              alt={avatarName}
              size="xl"
              shadow="sm"
              onError={() => {
                if (avatarFallbackSrc && currentAvatarSrc !== avatarFallbackSrc) {
                  setCurrentAvatarSrc(avatarFallbackSrc);
                }
              }}
            />
          </Grid>
          <Grid item>
            <MDBox height="100%" mt={0.5} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                {student.student_name || "Student Name"}
              </MDTypography>
              <MDTypography variant="button" color="text" fontWeight="regular">
                {branch.branch_name || "Branch"} - {batch.batch_name || "Batch"}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                Roll No: {student.student_rollno || "N/A"}
              </MDTypography>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4} sx={{ ml: "auto" }}>
            <AppBar position="static">
              <Tabs
                orientation={tabsOrientation}
                value={currentTabValue}
                onChange={handleSetTabValue}
              >
                <Tab
                  label="ATTENDANCE"
                  icon={
                    <Icon fontSize="small" sx={{ mt: -0.25 }}>
                      event_available
                    </Icon>
                  }
                />
                <Tab
                  label="FEE DUE"
                  icon={
                    <Icon fontSize="small" sx={{ mt: -0.25 }}>
                      account_balance_wallet
                    </Icon>
                  }
                />
                <Tab
                  label="RESULT"
                  icon={
                    <Icon fontSize="small" sx={{ mt: -0.25 }}>
                      workspace_premium
                    </Icon>
                  }
                />
              </Tabs>
            </AppBar>
          </Grid>
        </Grid>
        {children}
      </Card>
    </MDBox>
  );
}

HeaderNoMargin.defaultProps = {
  children: "",
  profileData: null,
  tabValue: undefined,
  onTabChange: undefined,
};

HeaderNoMargin.propTypes = {
  children: PropTypes.node,
  profileData: PropTypes.object,
  tabValue: PropTypes.number,
  onTabChange: PropTypes.func,
};

export default HeaderNoMargin;
